import React from "react";
import PassTemplate from "../Modals/PassTemplate";
import { useTranslation } from "react-i18next";
import { HiPhoto, HiTrash } from "react-icons/hi2";

interface DesignFormProps {
  activeTab: string;
  passTemplate: PassTemplate | null;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof PassTemplate,
    folder: "logos" | "icons" | "points" | "StripeBackgroundImage"
  ) => void;
  handleColorChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof PassTemplate
  ) => void;
  getImagePath: (url: string) => string;
  UpdateDesign: () => void;
  removeStripeImage: () => void;
}

interface ImageFieldProps {
  label: string;
  hint: string;
  inputId: string;
  required?: boolean;
  previewSrc?: string;
  previewClass?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
}

const ImageField: React.FC<ImageFieldProps> = ({ label, hint, inputId, required, previewSrc, previewClass = "w-16 h-16 object-contain", onChange, onRemove }) => (
  <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 flex items-center gap-4">
    <label htmlFor={inputId} className="flex-shrink-0 w-16 h-16 rounded-xl border-2 border-dashed border-brand-200 flex items-center justify-center cursor-pointer hover:border-brand-400 hover:bg-brand-50 transition overflow-hidden">
      {previewSrc ? (
        <img src={previewSrc} alt="" className={previewClass} />
      ) : (
        <HiPhoto className="w-6 h-6 text-brand-300" />
      )}
      <input id={inputId} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onChange} />
    </label>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-800">
        {label}
        {required && <span className="text-red-500 ms-1">*</span>}
      </p>
      <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
      <label htmlFor={inputId} className="mt-2 inline-block text-xs px-3 py-1 rounded-lg bg-brand-50 text-brand-600 font-semibold cursor-pointer hover:bg-brand-100 transition">
        {previewSrc ? "Change" : "Upload"}
      </label>
      {onRemove && previewSrc && (
        <button type="button" onClick={onRemove} className="ms-2 text-xs px-3 py-1 rounded-lg bg-red-50 text-red-500 font-semibold hover:bg-red-100 transition">
          <HiTrash className="inline w-3 h-3 me-1" />Remove
        </button>
      )}
    </div>
  </div>
);

const DesignForm: React.FC<DesignFormProps> = ({
  activeTab, passTemplate, handleFileChange, handleColorChange,
  getImagePath, UpdateDesign, removeStripeImage,
}) => {
  const { t } = useTranslation();

  const resolveUrl = (url?: string) => {
    if (!url) return undefined;
    return url.includes("googleapis") ? getImagePath(url) : url;
  };

  return (
    <div className={activeTab === "design" ? "block space-y-4 pt-4" : "hidden"}>

      <ImageField
        label={t("UploadLogoImage")}
        hint="PNG or JPG · max 480×150px"
        inputId="upload-logo"
        required
        previewSrc={resolveUrl(passTemplate?.logo)}
        previewClass="w-full h-full object-contain p-1"
        onChange={e => handleFileChange(e, "logo", "logos")}
      />

      <ImageField
        label={t("UploadIconImage")}
        hint="PNG or JPG · max 87×87px"
        inputId="upload-icon"
        required
        previewSrc={resolveUrl(passTemplate?.icon)}
        previewClass="w-full h-full object-contain p-1"
        onChange={e => handleFileChange(e, "icon", "icons")}
      />

      <ImageField
        label={t("UploadPointImage")}
        hint="PNG or JPG · max 200×200px"
        inputId="upload-point"
        required
        previewSrc={resolveUrl(passTemplate?.pointImage)}
        previewClass="w-full h-full object-contain p-1"
        onChange={e => handleFileChange(e, "pointImage", "points")}
      />

      <ImageField
        label={t("UploadStripeBackgroundImage")}
        hint="PNG or JPG · max 1125×432px · optional"
        inputId="upload-stripe"
        previewSrc={resolveUrl(passTemplate?.stripeBackgroundImage)}
        previewClass="w-full h-full object-cover rounded-lg"
        onChange={e => handleFileChange(e, "stripeBackgroundImage", "StripeBackgroundImage")}
        onRemove={removeStripeImage}
      />

      {/* Color pickers */}
      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-3">
        <p className="text-sm font-semibold text-gray-800">{t("colors")}</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t("PassBackgroundColor"), field: "backgroundColor" as keyof PassTemplate },
            { label: t("PassStripeBackgroundColor"), field: "stripeBackgroundColor" as keyof PassTemplate },
            { label: t("Pass Text Color"), field: "textColor" as keyof PassTemplate },
          ].map(({ label, field }) => (
            <div key={field} className="flex flex-col items-center gap-2">
              <input
                type="color"
                value={(passTemplate?.[field] as string) || "#ffffff"}
                onChange={e => handleColorChange(e, field)}
                className="w-12 h-12 rounded-xl border border-gray-200 cursor-pointer p-1 bg-white"
              />
              <p className="text-xs text-gray-500 text-center leading-tight">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={UpdateDesign}
        className="w-full py-3 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 transition active:scale-95"
      >
        {t("Next")}
      </button>
    </div>
  );
};

export default DesignForm;
